/**
 * Sign in with Apple on the web (GH-856, DEC-015) — the browser half.
 *
 * ## Apple's script is fetched on the tap, never on page load
 *
 * Sign in with Apple JS is a third-party script on Apple's CDN. Loading it with the page would tell
 * Apple the address of everyone who opens a donation page and *which* page — for this cohort an
 * inference about religious affiliation, the same reason the fonts are self-hosted (DESIGN.md).
 * So nothing is requested from Apple until the donor presses "Continue with Apple", at which point
 * they have chosen to talk to Apple.
 *
 * ## What this file trusts
 *
 * Nothing. It hands back Apple's ID token as an opaque string; the server verifies the signature
 * against Apple's keys, the issuer, the audience (our Services ID), the expiry and the single-use
 * nonce before a session exists. The display name Apple returns once, outside the token, is passed
 * along as a suggestion the server may use to fill an empty profile name.
 */
import type { DonorSignInOptions } from "@givehere/contracts";

const APPLE_JS =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

type AppleAuth = {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state: string;
    nonce: string;
    usePopup: boolean;
  }): void;
  signIn(): Promise<{
    authorization: { id_token: string; code?: string; state?: string };
    user?: { name?: { firstName?: string; lastName?: string } };
  }>;
};

type AppleGlobal = { AppleID?: { auth: AppleAuth } };

/**
 * The return address Apple must hand the result to, if one is registered on this page's own
 * origin. Apple refuses a mismatch outright, so offering the button without one would be offering
 * a button that fails.
 */
export function appleReturnAddress(
  options: DonorSignInOptions | null,
  origin: string = window.location.origin,
): string | null {
  if (!options?.apple) return null;
  return options.apple.redirectUris.find((uri) => new URL(uri).origin === origin) ?? null;
}

let loading: Promise<AppleAuth> | null = null;

function loadAppleJs(): Promise<AppleAuth> {
  const existing = (window as unknown as AppleGlobal).AppleID?.auth;
  if (existing) return Promise.resolve(existing);
  if (loading) return loading;
  loading = new Promise<AppleAuth>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPLE_JS;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      const auth = (window as unknown as AppleGlobal).AppleID?.auth;
      if (auth) resolve(auth);
      else reject(new Error("Sign in with Apple did not load."));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error("Sign in with Apple did not load."));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export type AppleAuthorization = { readonly idToken: string; readonly displayName: string | null };

/**
 * Open Apple's sheet and return its ID token. `nonce` is the server's; `state` is a fresh random
 * value checked on the way back, so a response that is not the answer to this tap is dropped.
 */
export async function authorizeWithApple(input: {
  clientId: string;
  redirectUri: string;
  nonce: string;
}): Promise<AppleAuthorization> {
  const auth = await loadAppleJs();
  const state = globalThis.crypto.randomUUID();
  auth.init({
    clientId: input.clientId,
    scope: "name email",
    redirectURI: input.redirectUri,
    state,
    nonce: input.nonce,
    usePopup: true,
  });
  const response = await auth.signIn();
  if (response.authorization.state !== state) {
    throw new Error("That sign-in was not the one this page started.");
  }
  const name = [response.user?.name?.firstName, response.user?.name?.lastName]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(" ")
    .trim();
  return { idToken: response.authorization.id_token, displayName: name || null };
}
